import React from 'react';
import { Card, Input, Radio, Button, Row, Col, Progress, Alert, Space } from 'antd';
import { toast } from '../../../utils/toast.js';
import {
  CheckCircleOutlined,
  InfoCircleOutlined,
  ShopOutlined,
  TeamOutlined,
  CalendarOutlined,
  LaptopOutlined,
  SafetyCertificateOutlined,
} from '@ant-design/icons';
import { PHARMACY_DIAGNOSTIC_QUESTIONS } from '../data/pharmacyToolData.js';

export function PharmacyDiagnosticForm({
  config,
  onChangeConfig,
  onSubmitDiagnostic,
  activeSopCount,
  totalSopCount,
}) {
  const handleChange = (field, value) => {
    const updated = {
      ...config,
      [field]: value,
    };

    // Tự động đồng bộ câu 3 và câu 4 nếu có mâu thuẫn về số lượng nhân sự
    if (field === 'staffCount') {
      if (value === '1') {
        updated.ownerPresence = 'chu-tu-ban';
      } else if (['2', '3-5', '6-10', '>10'].includes(value) && updated.ownerPresence === 'chu-tu-ban') {
        updated.ownerPresence = 'chu-cung-nhan-vien';
      }
    } else if (field === 'ownerPresence') {
      if (value === 'chu-tu-ban' && updated.staffCount !== '1') {
        updated.staffCount = '1';
      } else if (value !== 'chu-tu-ban' && updated.staffCount === '1') {
        updated.staffCount = '2';
      }
    }

    onChangeConfig(updated);
  };

  const handleSubmit = () => {
    const missing = [];
    if (!config.pharmacyType) missing.push('Loại hình (Câu 2)');
    if (!config.staffCount) missing.push('Số người làm (Câu 3)');
    if (!config.ownerPresence) missing.push('Chủ đứng quầy (Câu 4)');
    if (!config.shiftCount) missing.push('Số ca làm việc (Câu 5)');
    if (!config.hasSoftware) missing.push('Phần mềm quản lý (Câu 6)');

    if (missing.length > 0) {
      toast.warning({
        content: `Vui lòng chọn tiếp các câu: ${missing.join(', ')} để hệ thống chẩn đoán chính xác!`,
        duration: 4,
      });
      return;
    }

    if (onSubmitDiagnostic) {
      onSubmitDiagnostic(config);
    }
  };

  const getQuestionIcon = (id) => {
    switch (id) {
      case 'pharmacyName':
        return <ShopOutlined style={{ color: '#005537' }} />;
      case 'pharmacyType':
        return <SafetyCertificateOutlined style={{ color: '#0284c7' }} />;
      case 'staffCount':
        return <TeamOutlined style={{ color: '#7c3aed' }} />;
      case 'ownerPresence':
        return <ShopOutlined style={{ color: '#d97706' }} />;
      case 'shiftCount':
        return <CalendarOutlined style={{ color: '#16a34a' }} />;
      case 'hasSoftware':
        return <LaptopOutlined style={{ color: '#2563eb' }} />;
      default:
        return <InfoCircleOutlined />;
    }
  };

  // Calculate completion percentage
  const answeredCount = [
    config.pharmacyName,
    config.pharmacyType,
    config.staffCount,
    config.ownerPresence,
    config.shiftCount,
    config.hasSoftware,
  ].filter((v) => v && v !== '').length;
  const percentComplete = Math.min(100, Math.round((answeredCount / 6) * 100));

  return (
    <div>
      {/* Question Progress Banner */}
      <div
        style={{
          background: '#ffffff',
          border: '1px solid #e2e8f0',
          borderRadius: 12,
          padding: '16px 20px',
          marginBottom: 20,
          boxShadow: '0 1px 3px rgba(15, 23, 42, 0.04)',
        }}
      >
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: 8,
            flexWrap: 'wrap',
            gap: 8,
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ fontWeight: 700, fontSize: 14, color: '#0f172a' }}>
              Tiến Độ Trả Lời Câu Hỏi:
            </span>
            <span style={{ fontWeight: 800, fontSize: 14, color: '#005537' }}>
              {answeredCount}/6 câu ({percentComplete}%)
            </span>
          </div>
          <span style={{ fontSize: 13, color: '#64748b' }}>
            Đã kích hoạt: <strong style={{ color: '#005537' }}>{activeSopCount}</strong>/{totalSopCount} quy trình phù hợp
          </span>
        </div>
        <Progress
          percent={percentComplete}
          strokeColor="#005537"
          showInfo={false}
          style={{ margin: 0 }}
        />
      </div>

      <Row gutter={[24, 24]}>
        {/* Left Column: 6 Diagnostic Questions */}
        <Col xs={24} lg={15}>
          {PHARMACY_DIAGNOSTIC_QUESTIONS.map((q) => (
            <div key={q.id} className="survey-question-card">
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
                <div className="survey-q-num">{q.number}</div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  {getQuestionIcon(q.id)}
                  <span className="survey-q-title">{q.title}</span>
                </div>
              </div>

              {q.type === 'text' && (
                <Input
                  size="large"
                  placeholder={q.placeholder}
                  value={config[q.id] || ''}
                  onChange={(e) => handleChange(q.id, e.target.value)}
                  style={{ borderRadius: 8 }}
                />
              )}

              {q.type === 'select' && (
                <Radio.Group
                  value={config[q.id] || null}
                  onChange={(e) => handleChange(q.id, e.target.value)}
                  style={{ width: '100%' }}
                >
                  <Space direction="vertical" style={{ width: '100%' }} size={8}>
                    {q.options.map((opt) => (
                      <Radio
                        key={opt.value}
                        value={opt.value}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          padding: '10px 14px',
                          border: '1px solid',
                          borderColor: config[q.id] === opt.value ? '#005537' : '#e2e8f0',
                          borderRadius: 8,
                          background: config[q.id] === opt.value ? '#f0fdf4' : '#ffffff',
                          transition: 'all 0.15s ease',
                          marginRight: 0,
                          width: '100%',
                        }}
                      >
                        <span style={{ fontSize: 14, fontWeight: config[q.id] === opt.value ? 600 : 400, color: config[q.id] === opt.value ? '#005537' : '#1e293b' }}>
                          {opt.label}
                        </span>
                      </Radio>
                    ))}
                  </Space>
                </Radio.Group>
              )}
            </div>
          ))}

          <div
            style={{
              marginTop: 24,
              padding: '18px 20px',
              background: '#f8fafc',
              borderRadius: 10,
              border: '1px solid #cbd5e1',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              flexWrap: 'wrap',
              gap: 14,
            }}
          >
            <div>
              <div style={{ fontSize: 14, fontWeight: 700, color: '#0f172a' }}>
                {answeredCount === 6 ? 'Đã hoàn thành 6/6 câu hỏi chẩn đoán' : `Tiến độ: ${answeredCount}/6 câu hỏi đã trả lời`}
              </div>
              <div style={{ fontSize: 12.5, color: '#64748b', marginTop: 2 }}>
                Bấm nút bên cạnh để hệ thống phân tích và tự động xuất bản bộ quy trình phù hợp riêng cho cơ sở của bạn.
              </div>
            </div>

            <Button
              type="primary"
              size="large"
              icon={<CheckCircleOutlined />}
              onClick={handleSubmit}
              style={{
                background: '#005537',
                borderColor: '#005537',
                borderRadius: 8,
                fontWeight: 700,
                height: 48,
                padding: '0 28px',
                fontSize: 15,
                boxShadow: '0 4px 14px rgba(0, 85, 55, 0.25)',
              }}
            >
              Hoàn Tất Chẩn Đoán & Xem Quy Trình ({activeSopCount} QT)
            </Button>
          </div>
        </Col>

        {/* Right Column: Dynamic Diagnosis & Rollout Roadmap */}
        <Col xs={24} lg={9}>
          <div className="diagnostic-sidebar-sticky">
            {/* Dynamic Adaptation Alerts */}
            <Card
              title={
                <span style={{ fontWeight: 800, color: '#0f172a', display: 'flex', alignItems: 'center', gap: 8 }}>
                  <CheckCircleOutlined style={{ color: '#16a34a' }} />
                  Đặc Điểm Thích Ứng Của Quầy
                </span>
              }
              style={{ borderRadius: 12, marginBottom: 20, border: '1px solid #e2e8f0' }}
            >
              <Space direction="vertical" size={14} style={{ width: '100%' }}>
                {!config.staffCount && !config.ownerPresence && !config.hasSoftware && (
                  <div style={{ color: '#64748b', fontSize: 13.5, lineHeight: 1.5, padding: '8px 0' }}>
                    💡 Vui lòng trả lời các câu hỏi bên trái hoặc bấm xem nhanh trên Bản đồ để hệ thống tự động lọc chỉ hiển thị các quy trình mà cơ sở của bạn thực sự cần.
                  </div>
                )}

                {config.staffCount === '1' && (
                  <Alert
                    type="warning"
                    showIcon
                    message="Quầy 1 Người — Không Có Hệ Thống Nội Bộ"
                    description="Hệ thống tự động ẩn 6 quy trình quản lý nội bộ: QT-10 (Bàn giao ca), QT-11 (Kiểm quỹ), QT-20 (Tuyển dụng), QT-21 (Kèm việc), QT-22 (Giao việc từ xa), QT-23 (Xử lý vi phạm/nghỉ việc). Tinh gọn chỉ còn 20 quy trình thực tế bám sát việc bán hàng & quản lý quầy hàng ngày."
                  />
                )}
                {config.staffCount && config.staffCount !== '1' && (
                  <Alert
                    type="success"
                    showIcon
                    message={`Đội Ngũ Nhân Sự: ${config.staffCount} Người`}
                    description="Kích hoạt toàn diện nhóm Dòng Người (Tuyển dụng, Kèm 30 ngày, Giao việc khi vắng mặt, Xử lý khi nghỉ việc). Phân vai rõ ràng giữa Người bán, Quản lý và Dược sĩ chuyên môn."
                  />
                )}

                {config.ownerPresence === 'chu-tu-ban' && (
                  <Alert
                    type="info"
                    showIcon
                    message="Chủ Tự Đứng Bán"
                    description="Giọng văn trong quy trình chuyển sang tự nhắc mình. Ẩn QT-11 (Kiểm quỹ chéo) và QT-22 (Giao việc từ xa)."
                  />
                )}
                {config.ownerPresence && config.ownerPresence !== 'chu-tu-ban' && (
                  <Alert
                    type="info"
                    showIcon
                    message="Mô Hình Giao Quyền / Quản Lý"
                    description="Kích hoạt đầy đủ QT-11 (Kiểm quỹ & chống thất thoát) và QT-22 (Giao việc & kiểm việc khi chủ không có mặt bằng tin nhắn)."
                  />
                )}

                {config.hasSoftware === 'chua' && (
                  <Alert
                    type="warning"
                    showIcon
                    message="Chưa Có Phần Mềm Quản Lý"
                    description="Các bước nhập máy trong QT-01, QT-03, QT-04, QT-12 tự động đổi sang hướng dẫn ghi chép sổ tay tối thiểu để vẫn theo dõi được số lô và hạn dùng."
                  />
                )}
                {config.hasSoftware === 'co' && (
                  <Alert
                    type="success"
                    showIcon
                    message="Đã Ứng Dụng Phần Mềm Quản Lý"
                    description="Khai thác tối đa tính năng cảnh báo hạn dùng tự động, truy ngược khách hàng đã mua khi có thông báo thu hồi thuốc."
                  />
                )}
              </Space>
            </Card>
          </div>
        </Col>
      </Row>
    </div>
  );
}
